import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  fetchTree,
  createFolder,
  updateFolder,
  deleteFolder,
  createDocument,
  fetchDocument,
  updateDocument,
  deleteDocument,
  createGroup,
  createRow,
  updateRow,
  deleteRow,
} from '../../store/slices/reportingSlice';
import FileTree, { ChartIcon } from './FileTree';
import DocumentView from './DocumentView';
import {
  NewItemModal,
  RenameModal,
  AddGroupModal,
  RowEditModal,
  ShareDocModal,
} from './ReportingModals';
import './ReportingPage.css';

const ZOOM_LEVELS = [
  { key: 'weeks', label: 'Weeks', pxPerDay: 20 },
  { key: 'months', label: 'Months', pxPerDay: 8 },
  { key: 'quarters', label: 'Quarters', pxPerDay: 3.5 },
];

const ReportingPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { docId } = useParams();

  const { tree, currentDoc, loading } = useSelector(s => s.reporting);
  const { user: currentUser } = useSelector(s => s.auth);

  const [activeDocId, setActiveDocId] = useState(docId ? parseInt(docId, 10) : null);
  const [treeCollapsed, setTreeCollapsed] = useState(!!docId);
  const [view, setView] = useState('gantt');
  const [zoom, setZoom] = useState('weeks');
  const [modals, setModals] = useState({
    newItem: false, rename: false, addGroup: false, rowEdit: false,
  });
  const [renameNode, setRenameNode] = useState(null);
  const [newItemCtx, setNewItemCtx] = useState({ parentId: null, type: 'doc' });
  const [docTitle, setDocTitle] = useState('');
  const [editRow, setEditRow] = useState(null);
  const [shareDocNode, setShareDocNode] = useState(null);
  const [listingFolder, setListingFolder] = useState(null); // null = root
  const [listingPath, setListingPath] = useState([]);
  const [listingSearch, setListingSearch] = useState('');
  const titleSaveRef = useRef(null);

  useEffect(() => {
    dispatch(fetchTree());
  }, [dispatch]);

  useEffect(() => {
    if (docId) {
      const id = parseInt(docId, 10);
      setActiveDocId(id);
      dispatch(fetchDocument({ id }));
    } else {
      setActiveDocId(null);
    }
  }, [docId, dispatch]);

  useEffect(() => {
    if (currentDoc) setDocTitle(currentDoc.name);
  }, [currentDoc?.id]); // eslint-disable-line

  const openModal = (key) => setModals(m => ({ ...m, [key]: true }));
  const closeModal = (key) => setModals(m => ({ ...m, [key]: false }));

  // ── Listing navigation ───────────────────────────────────────────────────────

  const listingItems = useMemo(() => {
    const q = listingSearch.toLowerCase();
    let folders = listingFolder ? (listingFolder.children || []) : (tree.folders || []);
    let docs = listingFolder ? (listingFolder.documents || []) : (tree.loose_documents || []);
    if (q) {
      folders = folders.filter(f => f.name.toLowerCase().includes(q));
      docs = docs.filter(d => d.name.toLowerCase().includes(q));
    }
    return { folders, docs };
  }, [tree, listingFolder, listingSearch]);

  const handleNavigateIntoFolder = useCallback((folder) => {
    setListingFolder(folder);
    setListingPath(prev => [...prev, { id: folder.id, name: folder.name, data: folder }]);
    setListingSearch('');
  }, []);

  const handleListingBreadcrumbNav = useCallback((idx) => {
    if (idx < 0) {
      setListingFolder(null);
      setListingPath([]);
    } else {
      setListingFolder(listingPath[idx].data);
      setListingPath(prev => prev.slice(0, idx + 1));
    }
    setListingSearch('');
  }, [listingPath]);

  const formatRelDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - d) / 86400000);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Yesterday';
    if (diff < 7) return `${diff}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: diff > 365 ? 'numeric' : undefined });
  };

  // ── File tree handlers ──────────────────────────────────────────────────────

  const handleSelectDoc = useCallback((node) => {
    if (node.type !== 'doc') return;
    setTreeCollapsed(true);
    navigate(`/reporting/d/${node.id}`);
  }, [navigate]);

  const handleToggleFolder = useCallback((folder) => {
    dispatch(updateFolder({ id: folder.id, data: { expanded: !folder.expanded } }));
  }, [dispatch]);

  const handleNewItem = useCallback((parentId, type) => {
    setNewItemCtx({ parentId, type });
    openModal('newItem');
  }, []); // eslint-disable-line

  const handleRename = useCallback((node) => {
    setRenameNode(node);
    openModal('rename');
  }, []); // eslint-disable-line

  const handleDelete = useCallback(async (node) => {
    if (!window.confirm(`Delete ${node.type === 'folder' ? 'folder' : 'document'} "${node.name}"?`)) return;
    if (node.type === 'folder') {
      try {
        await dispatch(deleteFolder({ id: node.id })).unwrap();
        toast.success('Folder deleted');
        dispatch(fetchTree());
      } catch (e) {
        toast.error(e || 'Failed to delete folder');
      }
    } else {
      try {
        await dispatch(deleteDocument({ id: node.id })).unwrap();
        toast.success('Document deleted');
        if (activeDocId === node.id) {
          setActiveDocId(null);
          navigate('/reporting');
        }
        dispatch(fetchTree());
      } catch (e) {
        toast.error(e || 'Failed to delete document');
      }
    }
  }, [dispatch, activeDocId, navigate]);

  // ── Create item ─────────────────────────────────────────────────────────────

  const handleCreateItem = useCallback(async ({ type, name, chartType, parentId }) => {
    try {
      if (type === 'folder') {
        await dispatch(createFolder({ name, parentId })).unwrap();
        toast.success('Folder created');
        dispatch(fetchTree());
      } else {
        const doc = await dispatch(createDocument({
          name,
          folderId: parentId || null,
          chartType: chartType || 'gantt',
        })).unwrap();
        toast.success('Document created');
        dispatch(fetchTree());
        navigate(`/reporting/d/${doc.id}`);
      }
    } catch (e) {
      toast.error(e || 'Failed to create item');
    }
    closeModal('newItem');
  }, [dispatch, navigate]); // eslint-disable-line

  // ── Rename ──────────────────────────────────────────────────────────────────

  const handleRenameConfirm = useCallback(async (id, type, newName) => {
    try {
      if (type === 'folder') {
        await dispatch(updateFolder({ id, data: { name: newName } })).unwrap();
      } else {
        await dispatch(updateDocument({ id, data: { name: newName } })).unwrap();
      }
      toast.success('Renamed');
      dispatch(fetchTree());
      if (currentDoc?.id === id) setDocTitle(newName);
    } catch (e) {
      toast.error(e || 'Failed to rename');
    }
    closeModal('rename');
  }, [dispatch, currentDoc?.id]); // eslint-disable-line

  // ── Title inline edit ───────────────────────────────────────────────────────

  const handleTitleChange = (e) => {
    setDocTitle(e.target.value);
    clearTimeout(titleSaveRef.current);
    titleSaveRef.current = setTimeout(async () => {
      if (!currentDoc || !e.target.value.trim()) return;
      try {
        await dispatch(updateDocument({ id: currentDoc.id, data: { name: e.target.value.trim() } })).unwrap();
        dispatch(fetchTree());
      } catch (_) {}
    }, 800);
  };

  // ── Add group ───────────────────────────────────────────────────────────────

  const handleAddGroup = useCallback(async ({ name, color }) => {
    if (!currentDoc) return;
    try {
      await dispatch(createGroup({ docId: currentDoc.id, data: { name, color } })).unwrap();
      toast.success('Group added');
    } catch (e) {
      toast.error(e || 'Failed to add group');
    }
    closeModal('addGroup');
  }, [dispatch, currentDoc]); // eslint-disable-line

  // ── Add global milestone ────────────────────────────────────────────────────

  const handleAddGlobalMilestone = useCallback(async () => {
    if (!currentDoc) return;
    let milestoneGroup = currentDoc.groups.find(g => g.is_milestone_group);
    if (!milestoneGroup) {
      try {
        milestoneGroup = await dispatch(createGroup({
          docId: currentDoc.id,
          data: { name: 'Milestones', color: '#f59e0b', is_milestone_group: true },
        })).unwrap();
      } catch (e) {
        toast.error('Failed to create milestone group');
        return;
      }
    }
    const todayIso = new Date().toISOString().split('T')[0];
    setEditRow({
      _isNew: true,
      groupId: milestoneGroup.id,
      is_milestone: true,
      name: 'New Milestone',
      start_date: todayIso,
      end_date: todayIso,
      status: 'not-started',
      percent_complete: 0,
      custom_fields: {},
    });
    openModal('rowEdit');
  }, [dispatch, currentDoc]); // eslint-disable-line

  // ── Row edit ────────────────────────────────────────────────────────────────

  const handleEditRow = useCallback((row) => {
    setEditRow(row);
    openModal('rowEdit');
  }, []); // eslint-disable-line

  const handleRowSave = useCallback(async (rowId, data) => {
    try {
      if (!rowId) {
        await dispatch(createRow({ groupId: editRow.groupId, data })).unwrap();
        toast.success('Task added');
      } else {
        await dispatch(updateRow({ id: rowId, data })).unwrap();
        toast.success('Saved');
      }
      closeModal('rowEdit');
    } catch (e) {
      toast.error(e || 'Failed to save');
    }
  }, [dispatch, editRow]); // eslint-disable-line

  const handleRowDelete = useCallback(async (rowId) => {
    try {
      await dispatch(deleteRow({ id: rowId })).unwrap();
      toast.success('Row deleted');
    } catch (e) {
      toast.error(e || 'Failed to delete');
    }
  }, [dispatch]);

  // ── Zoom ────────────────────────────────────────────────────────────────────

  const zoomIdx = ZOOM_LEVELS.findIndex(z => z.key === zoom);
  const zoomIn = () => { if (zoomIdx < ZOOM_LEVELS.length - 1) setZoom(ZOOM_LEVELS[zoomIdx + 1].key); };
  const zoomOut = () => { if (zoomIdx > 0) setZoom(ZOOM_LEVELS[zoomIdx - 1].key); };

  if (!currentUser?.reporting_access) {
    return (
      <div className="rp-access-denied">
        <div className="rp-access-denied-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="48" height="48">
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
          </svg>
        </div>
        <h2 className="rp-access-denied-title">Reporting Access Required</h2>
        <p className="rp-access-denied-sub">You don't have access to the Reporting module. Contact your administrator to request access.</p>
      </div>
    );
  }

  return (
    <div className={`reporting-page${treeCollapsed ? ' tree-collapsed' : ''}`}>
      {/* Left: file tree — always visible */}
      <FileTree
        tree={tree}
        activeDocId={activeDocId}
        onSelectDoc={handleSelectDoc}
        onToggleFolder={handleToggleFolder}
        onNewItem={handleNewItem}
        onRename={handleRename}
        onDelete={handleDelete}
        onShare={(node) => { setShareDocNode(node); }}
      />

      {/* Right/Main area */}
      <div className="rp-main">

        {/* ── Listing mode (no doc open) ─────────────────────────────────────── */}
        {!activeDocId && (
          <div className="rp-listing">
            {/* Header */}
            <div className="rp-listing-header">
              <div className="rp-listing-header-left">
                <h1 className="rp-listing-title">Reporting</h1>
                <span className="rp-listing-count">
                  {listingItems.folders.length + listingItems.docs.length} item{listingItems.folders.length + listingItems.docs.length !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="rp-listing-header-right">
                <div className="rp-listing-search">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M11.742 10.344a6.5 6.5 0 10-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 001.415-1.414l-3.85-3.85a1.007 1.007 0 00-.115-.1zM12 6.5a5.5 5.5 0 11-11 0 5.5 5.5 0 0111 0z"/>
                  </svg>
                  <input
                    value={listingSearch}
                    onChange={e => setListingSearch(e.target.value)}
                    placeholder="Search reports…"
                  />
                </div>
                <button
                  className="rp-btn rp-btn-secondary rp-btn-sm"
                  onClick={() => { setNewItemCtx({ parentId: listingFolder?.id || null, type: 'folder' }); openModal('newItem'); }}
                >
                  <svg viewBox="0 0 16 16" fill="currentColor" width="13" height="13">
                    <path d="M9.828 3h3.982a2 2 0 011.992 2.192l-.522 4.957A2 2 0 0113.292 12H3.706a2 2 0 01-1.982-1.851l-.395-5.93A2 2 0 013.31 2h6.516a1 1 0 01.707.293L9.828 3z"/>
                  </svg>
                  New Folder
                </button>
                <button
                  className="rp-btn rp-btn-primary rp-btn-sm"
                  onClick={() => { setNewItemCtx({ parentId: listingFolder?.id || null, type: 'doc' }); openModal('newItem'); }}
                >
                  <svg viewBox="0 0 16 16" fill="currentColor" width="13" height="13">
                    <path d="M8 4a.5.5 0 01.5.5v3h3a.5.5 0 010 1h-3v3a.5.5 0 01-1 0v-3h-3a.5.5 0 010-1h3v-3A.5.5 0 018 4z"/>
                  </svg>
                  New Report
                </button>
              </div>
            </div>

            {/* Breadcrumbs */}
            <div className="rp-listing-breadcrumbs">
              <button
                className={`rp-listing-bc-item${listingPath.length === 0 ? ' current' : ''}`}
                onClick={() => handleListingBreadcrumbNav(-1)}
              >
                <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M8.354 1.146a.5.5 0 00-.708 0l-6 6A.5.5 0 001.5 7.5v7a.5.5 0 00.5.5h4.5a.5.5 0 00.5-.5v-4h2v4a.5.5 0 00.5.5H14a.5.5 0 00.5-.5v-7a.5.5 0 00-.146-.354L8.354 1.146z"/>
                </svg>
                Reporting
              </button>
              {listingPath.map((seg, i) => (
                <React.Fragment key={seg.id}>
                  <span className="rp-listing-bc-sep">/</span>
                  <button
                    className={`rp-listing-bc-item${i === listingPath.length - 1 ? ' current' : ''}`}
                    onClick={() => handleListingBreadcrumbNav(i)}
                  >
                    {seg.name}
                  </button>
                </React.Fragment>
              ))}
            </div>

            {/* Content */}
            <div className="rp-listing-content">
              {listingItems.folders.length === 0 && listingItems.docs.length === 0 ? (
                <div className="rp-listing-empty">
                  <div className="rp-listing-empty-icon">
                    <svg viewBox="0 0 48 48" fill="currentColor" width="40" height="40">
                      <path d="M6 8a4 4 0 014-4h12l4 4h16a4 4 0 014 4v24a4 4 0 01-4 4H10a4 4 0 01-4-4V8zm4-2a2 2 0 00-2 2v28a2 2 0 002 2h28a2 2 0 002-2V14h-14l-4-4H10V6z" opacity=".4"/>
                      <path d="M24 18v12M18 24h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <p className="rp-listing-empty-title">
                    {listingSearch ? `No results for "${listingSearch}"` : 'No reports yet'}
                  </p>
                  {!listingSearch && (
                    <p className="rp-listing-empty-sub">Create a folder to organise your reports, or add your first report.</p>
                  )}
                </div>
              ) : (
                <div className="rp-listing-grid">
                  {/* Folders */}
                  {listingItems.folders.map(folder => (
                    <div
                      key={`f-${folder.id}`}
                      className="rp-listing-card rp-listing-card-folder"
                      onClick={() => handleNavigateIntoFolder(folder)}
                    >
                      <div className="rp-listing-card-icon folder-icon">
                        <svg viewBox="0 0 16 16" fill="currentColor" width="22" height="22">
                          <path d="M9.828 3h3.982a2 2 0 011.992 2.192l-.522 4.957A2 2 0 0113.292 12H3.706a2 2 0 01-1.982-1.851l-.395-5.93A2 2 0 013.31 2h6.516a1 1 0 01.707.293L9.828 3z"/>
                        </svg>
                      </div>
                      <div className="rp-listing-card-body">
                        <span className="rp-listing-card-name">{folder.name}</span>
                        <span className="rp-listing-card-meta">
                          {((folder.children || []).length + (folder.documents || []).length)} item{((folder.children || []).length + (folder.documents || []).length) !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="rp-listing-card-actions" onClick={e => e.stopPropagation()}>
                        <button
                          className="rp-listing-card-action-btn"
                          title="Rename"
                          onClick={() => handleRename({ ...folder, type: 'folder' })}
                        >
                          <svg viewBox="0 0 16 16" fill="currentColor" width="12" height="12">
                            <path d="M12.146.146a.5.5 0 01.708 0l3 3a.5.5 0 010 .708l-10 10a.5.5 0 01-.168.11l-5 2a.5.5 0 01-.65-.65l2-5a.5.5 0 01.11-.168l10-10z"/>
                          </svg>
                        </button>
                        <button
                          className="rp-listing-card-action-btn danger"
                          title="Delete"
                          onClick={() => handleDelete({ ...folder, type: 'folder' })}
                        >
                          <svg viewBox="0 0 16 16" fill="currentColor" width="12" height="12">
                            <path d="M5.5 5.5A.5.5 0 016 6v6a.5.5 0 01-1 0V6a.5.5 0 01.5-.5zm2.5 0a.5.5 0 01.5.5v6a.5.5 0 01-1 0V6a.5.5 0 01.5-.5zm3 .5a.5.5 0 00-1 0v6a.5.5 0 001 0V6z"/>
                            <path d="M14.5 3a1 1 0 01-1 1H13v9a2 2 0 01-2 2H5a2 2 0 01-2-2V4h-.5a1 1 0 010-2h11a1 1 0 011 1zm-8.5 0v-.5h4V3h-4z"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* Reports */}
                  {listingItems.docs.map(doc => (
                    <div
                      key={`d-${doc.id}`}
                      className="rp-listing-card rp-listing-card-report"
                      onClick={() => handleSelectDoc({ ...doc, type: 'doc' })}
                    >
                      <div className="rp-listing-card-icon report-icon">
                        <ChartIcon type={doc.chart_type || 'gantt'} size={20} />
                      </div>
                      <div className="rp-listing-card-body">
                        <span className="rp-listing-card-name">{doc.name}</span>
                        <span className="rp-listing-card-meta">{formatRelDate(doc.updated_at)}</span>
                      </div>
                      <div className="rp-listing-card-actions" onClick={e => e.stopPropagation()}>
                        <button
                          className="rp-listing-card-action-btn"
                          title="Rename"
                          onClick={() => handleRename({ ...doc, type: 'doc' })}
                        >
                          <svg viewBox="0 0 16 16" fill="currentColor" width="12" height="12">
                            <path d="M12.146.146a.5.5 0 01.708 0l3 3a.5.5 0 010 .708l-10 10a.5.5 0 01-.168.11l-5 2a.5.5 0 01-.65-.65l2-5a.5.5 0 01.11-.168l10-10z"/>
                          </svg>
                        </button>
                        <button
                          className="rp-listing-card-action-btn danger"
                          title="Delete"
                          onClick={() => handleDelete({ ...doc, type: 'doc' })}
                        >
                          <svg viewBox="0 0 16 16" fill="currentColor" width="12" height="12">
                            <path d="M5.5 5.5A.5.5 0 016 6v6a.5.5 0 01-1 0V6a.5.5 0 01.5-.5zm2.5 0a.5.5 0 01.5.5v6a.5.5 0 01-1 0V6a.5.5 0 01.5-.5zm3 .5a.5.5 0 00-1 0v6a.5.5 0 001 0V6z"/>
                            <path d="M14.5 3a1 1 0 01-1 1H13v9a2 2 0 01-2 2H5a2 2 0 01-2-2V4h-.5a1 1 0 010-2h11a1 1 0 011 1zm-8.5 0v-.5h4V3h-4z"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Document view ─────────────────────────────────────────────────── */}
        {activeDocId && (loading && !currentDoc) && (
          <div className="rp-loading">Loading document…</div>
        )}
        {activeDocId && currentDoc && (
          <DocumentView
            doc={currentDoc}
            docTitle={docTitle}
            view={view}
            zoom={zoom}
            zoomLabel={ZOOM_LEVELS.find(z => z.key === zoom)?.label}
            zoomIdx={zoomIdx}
            zoomLevels={ZOOM_LEVELS}
            treeCollapsed={treeCollapsed}
            onToggleTree={() => setTreeCollapsed(c => !c)}
            onTitleChange={handleTitleChange}
            onViewChange={setView}
            onZoomIn={zoomIn}
            onZoomOut={zoomOut}
            onAddGroup={() => openModal('addGroup')}
            onAddMilestone={handleAddGlobalMilestone}
            onEditRow={handleEditRow}
          />
        )}
        {activeDocId && !loading && !currentDoc && (
          <div className="rp-loading">Loading document…</div>
        )}
      </div>

      {/* Modals */}
      <NewItemModal
        isOpen={modals.newItem}
        onClose={() => closeModal('newItem')}
        onSave={handleCreateItem}
        defaultType={newItemCtx.type}
        parentId={newItemCtx.parentId}
      />
      <RenameModal
        isOpen={modals.rename}
        onClose={() => closeModal('rename')}
        node={renameNode}
        onSave={handleRenameConfirm}
      />
      <AddGroupModal
        isOpen={modals.addGroup}
        onClose={() => closeModal('addGroup')}
        onSave={handleAddGroup}
      />
      <RowEditModal
        isOpen={modals.rowEdit}
        onClose={() => closeModal('rowEdit')}
        row={editRow}
        groups={currentDoc?.groups || []}
        onSave={handleRowSave}
        onDelete={handleRowDelete}
      />
      <ShareDocModal
        isOpen={!!shareDocNode}
        onClose={() => setShareDocNode(null)}
        docId={shareDocNode?.id}
        docName={shareDocNode?.name}
      />
    </div>
  );
};

export default ReportingPage;
